import { useQuery } from "@tanstack/react-query";
import { api } from "@/services/api";
import { useCan } from "@/auth/permissions";
import { useEdition } from "@/theme/useTheme";

/**
 * MKT-1 · 内容营销（个人站订阅版）数据层——后端 routes/mkt_marketing.py（/api/v1/marketing/*）+ mkt_media_license.py。
 * 🔴 仅个人站：本目录整体在公司站导出时删除（infra/company/export_to_utour.sh）；共用文件里的引用都包在 COMPANY-STRIP 标记里。
 * 🔴 「未知不装正常」：S4 拿不到的数字后端给 null + label，这里原样透传，界面显示「未知」，绝不拿 0 顶。
 * 每个函数对应一个后端端点（铁律十三 API-First），见同目录 API_MAP.md。
 */

export interface S4Meta {
  ok: boolean;
  code: string | null;
  label: string | null;
  stale?: boolean;
  retry_after?: number | null;
  generated_at?: string | null;
}

export interface MarketingStatus {
  edition: "enterprise" | "subscription";
  enabled: boolean;
  s4_configured: boolean;
  s4_label: string | null;
}

export interface MarketingOverview {
  window: "7d" | "30d";
  posts_published: number | null;
  posts_scheduled: number | null;
  posts_failed: number | null;
  products_covered: number | null;
  s4: S4Meta;
  visits: number;
  visits_window_from: string;
  orders: number;
  orders_cancelled: number;
  orders_amount_cents: number | null;
  orders_amount_complete: boolean;
  orders_from: string;
  orders_to: string;
  products_open: number;
  products_total: number;
}

export type PostStatus = "draft" | "scheduled" | "published" | "failed" | "cancelled";

export interface MarketingPost {
  post_id: string;
  post_group_id: string | null;
  product_ref: string | null;
  product_code: string | null;
  product_name: string | null;
  product_label: string | null;
  platform: string;
  channel_name: string | null;
  status: string;
  status_label: string;
  deleted_in_s4: boolean;
  publish_date: string | null;
  release_url: string | null;
  failure_reason: string | null;
  attribution_ref: string | null;
  orders: number | null;
  orders_amount_cents: number | null;
  orders_amount_complete: boolean | null;
  visits: number | null;
}

export interface MarketingPosts {
  items: MarketingPost[];
  next_cursor: string | null;
  s4: S4Meta;
  available: boolean;
}

export interface SocialProduct {
  product_id: string;
  code: string;
  name_cn: string;
  social_enabled: boolean;
  social_first_enabled_at: string | null;
  external_id: string;
  mounted_posts: number | null;
  images_licensed: number;
  images_undeclared: number;
  images_not_licensed: number;
}

export interface SocialProducts {
  items: SocialProduct[];
  counts: { all: number; open: number };
  s4: S4Meta;
}

export interface SsoResult {
  ok: boolean;
  ticket_url: string | null;
  expires_at: string | null;
  code: string | null;
  label: string | null;
}

export const MKT_QK = {
  all: ["marketing"] as const,
  status: ["marketing", "status"] as const,
  overview: (w: string) => ["marketing", "overview", w] as const,
  posts: (status: string, cursor: string | null) => ["marketing", "posts", status, cursor] as const,
  products: ["marketing", "social-products"] as const,
};

export const marketingApi = {
  status: (): Promise<MarketingStatus> => api("/api/v1/marketing/status"),
  overview: (window: "7d" | "30d" = "7d"): Promise<MarketingOverview> => api(`/api/v1/marketing/overview?window=${window}`),
  posts: (status: PostStatus | "" = "", cursor: string | null = null): Promise<MarketingPosts> => {
    const p = new URLSearchParams();
    if (status) p.set("status", status);
    if (cursor) p.set("cursor", cursor);
    const qs = p.toString();
    return api(`/api/v1/marketing/posts${qs ? `?${qs}` : ""}`);
  },
  socialProducts: (): Promise<SocialProducts> => api("/api/v1/marketing/social-products"),
  setSocial: (productId: string, enabled: boolean): Promise<{ product_id: string; social_enabled: boolean; changed: boolean; warnings?: string[] }> =>
    api(`/api/v1/marketing/social-products/${productId}`, { method: "PUT", body: JSON.stringify({ social_enabled: enabled }) }),
  // S4 V1.2 · 带 postId = 直达这条帖子的「发布回执与效果」页；不带 = 进 S4 日历
  sso: (postId?: string): Promise<SsoResult> =>
    api("/api/v1/marketing/s4/sso", postId ? { method: "POST", body: JSON.stringify({ post_id: postId }) } : { method: "POST" }),
  setLicensed: (assetId: string, licensed: boolean | null): Promise<{ updated: number }> =>
    api(`/api/v1/media-library/${assetId}/licensed`, { method: "PATCH", body: JSON.stringify({ licensed }) }),
  setLicensedBatch: (assetIds: string[], licensed: boolean | null): Promise<{ updated: number; unchanged: number }> =>
    api("/api/v1/media-library:set-licensed", { method: "POST", body: JSON.stringify({ asset_ids: assetIds, licensed }) }),
};

/** 模块状态：只有订阅版才去问后端（企业版零请求、零变化）；没有 marketing.content.view 的账号也不问（菜单本来就不显示）。 */
export function useMarketingStatus() {
  const edition = useEdition();
  const canView = useCan("marketing.content.view");
  return useQuery({ queryKey: MKT_QK.status, queryFn: marketingApi.status, enabled: edition === "subscription" && canView, staleTime: 60_000 });
}

/** 内容营销已开通（订阅版 + enabled_modules 含内容营销 + 有查看权限）。拿不准（加载中 / 出错）= false：不显示入口，不放死链。 */
export function useMarketingEnabled(): boolean {
  const q = useMarketingStatus();
  return q.data?.enabled === true;
}

/** 数字显示：null / undefined = 「未知」（S4 拿不到），绝不显示 0。 */
export function showNum(n: number | null | undefined): string {
  return n === null || n === undefined ? "未知" : String(n);
}

export function money(cents: number | null | undefined): string {
  return cents === null || cents === undefined ? "—" : "$" + Math.round(cents / 100).toLocaleString("en-US");
}

/** 「数据时间」：取 S4 响应的 generatedAt（契约 §5.5）。 */
export function dataTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("zh-CN", { month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}

/** 打开 S4 控制台：先同步开空窗口（避免浏览器拦截弹窗），拿到一次性链接后再跳；失败关窗并把人话错误交给调用方。 */
export async function openS4Console(postId?: string): Promise<string | null> {
  const win = typeof window !== "undefined" ? window.open("about:blank", "_blank") : null;
  if (win) win.opener = null;   // 不让 S4 页面拿到本窗口引用
  try {
    const r = await marketingApi.sso(postId);
    if (r.ok && r.ticket_url) {
      if (win) win.location.href = r.ticket_url;
      else window.location.href = r.ticket_url;
      return null;
    }
    win?.close();
    return r.label ?? "未知";
  } catch (e) {
    win?.close();
    return e instanceof Error ? e.message : "申请失败，请重试";
  }
}
